/**
 * ActiveWorkItemBar — UNIFIED_WORK_LIFECYCLE_CONTEXT_PERSISTENCE_V1
 *
 * Visible "current work item" bar that shows persisted lifecycle context.
 * Renders whenever a saved context exists. Provides a clear/reset action.
 *
 * BOUNDARY:
 * - persisted context = navigation memory
 * - persisted context ≠ approval
 * - persisted context ≠ execution authority
 * - clear action only removes navigation memory, no operational effect
 */

import React, { useState } from 'react';
import { X, AlertTriangle, ChevronRight } from 'lucide-react';
import { useWorkLifecycleContext } from './useWorkLifecycleContext';
import type { LifecycleStage } from './WorkLifecycleNav';

interface ActiveWorkItemBarProps {
  /** Current page stage — reserved for future stage-aware display */
  currentStage?: LifecycleStage;
  className?: string;
}

function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 12)}…` : id;
}

export const ActiveWorkItemBar: React.FC<ActiveWorkItemBarProps> = ({
  currentStage: _currentStage,
  className = '',
}) => {
  const { context, health, clear } = useWorkLifecycleContext();
  const [confirmClear, setConfirmClear] = useState(false);

  if (!health.hasContext) return null;

  const handleClearClick = () => {
    if (confirmClear) {
      clear();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
    }
  };

  const handleCancelClear = () => setConfirmClear(false);

  const savedAt = context.savedAt
    ? new Date(context.savedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2 border-b text-xs ${
        health.isStale
          ? 'bg-amber-950/40 border-amber-800/40'
          : 'bg-slate-900/80 border-slate-800'
      } ${className}`}
    >
      {/* Stale warning */}
      {health.isStale && (
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
      )}

      {/* Label / identifier chain */}
      <span className="text-slate-400 flex-shrink-0">Active work:</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {context.label && (
          <span className="text-white font-medium">{context.label}</span>
        )}
        {context.requestId && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-300 font-mono">
            req:{shortId(context.requestId)}
          </span>
        )}
        {context.gwuId && (
          <>
            {context.requestId && <ChevronRight className="w-3 h-3 text-slate-600" />}
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-300 font-mono">
              gwu:{shortId(context.gwuId)}
            </span>
          </>
        )}
        {context.receiptId && (
          <>
            <ChevronRight className="w-3 h-3 text-slate-600" />
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-300 font-mono">
              rcpt:{shortId(context.receiptId)}
            </span>
          </>
        )}
        {context.relationshipId && (
          <>
            <ChevronRight className="w-3 h-3 text-slate-600" />
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-300 font-mono">
              rel:{shortId(context.relationshipId)}
            </span>
          </>
        )}
      </div>

      {/* Saved timestamp */}
      {savedAt && (
        <span className="text-slate-600 hidden sm:inline flex-shrink-0">
          saved {savedAt}
        </span>
      )}

      {/* Stale notice */}
      {health.isStale && (
        <span className="text-amber-500 flex-shrink-0">
          (stale — may not reflect current state)
        </span>
      )}

      {/* Clear action */}
      <div className="ml-auto flex items-center gap-2 flex-shrink-0">
        {confirmClear ? (
          <>
            <span className="text-slate-400">Clear navigation context?</span>
            <button
              onClick={handleClearClick}
              className="px-2 py-0.5 bg-red-900/50 hover:bg-red-900 border border-red-700/50 text-red-300 rounded transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleCancelClear}
              className="px-2 py-0.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded transition-colors"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={handleClearClick}
            title="Clear navigation context"
            className="p-1 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
